// src/scenes/groupPositions/index.jsx
import React, { useState } from "react";
import { Box, useTheme, useMediaQuery, Snackbar, Alert, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation, gql } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import Header from "components/Header";
import GroupPositionForm from "./form";
import { formatDisplayDate } from "../../utils/date";

const GET_GROUP_POSITIONS = gql`
  query {
    groupPositions {
      id
      positionName
      description
      groupName
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_GROUP_POSITION = gql`
  mutation CreateGroupPosition($newPosition: GroupPositionsInput!) {
    createGroupPosition(newPosition: $newPosition) {
      id
      positionName
      description
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;


const GroupPositions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data, loading, refetch } = useQuery(GET_GROUP_POSITIONS);
  const [createPosition] = useMutation(CREATE_GROUP_POSITION);

  const [formData, setFormData] = useState({ positionName: "", description: "" });
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async () => {
    try {
      const variables = { newPosition: formData };
      const { data } = await createPosition({ variables });

      if (data?.createGroupPosition?.id) {
        await refetch();
        setSuccessMessage("Position created successfully");
        setOpen(false);
      } else {
        alert("Failed to create group position.");
      }
    } catch (err) {
      console.error(err);
      alert("Error occurred while creating position.");
    }
  };

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "positionName", headerName: "Position Name", flex: 1 },
    { field: "description", headerName: "Description", flex: 2 },
    { field: "groupName", headerName: "Group", flex: 1.5 },
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 1,
      valueFormatter: ({ value }) => formatDisplayDate(value),
    },
  ];

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="GROUP POSITIONS" subtitle="Roles defined within your group" />

      <GroupPositionForm
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Create Position
        </Button>
      </Box>

      <Box mt="40px" height="75vh" sx={{
        "& .MuiDataGrid-root": { border: "none" },
        "& .MuiDataGrid-cell": { borderBottom: "none" },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.secondary[100],
          borderBottom: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: theme.palette.primary.light,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.secondary[100],
          borderTop: "none",
        },
      }}>
        <DataGrid
          loading={loading}
          rows={(data?.groupPositions || []).map((row, index) => ({ ...row, sequence: index + 1 }))}
          getRowId={(row) => row.id}
          columns={columns}
        
          columnVisibilityModel={
            isMobile
              ? {
                description: false,
              }
              : undefined
          }/>
      </Box>
    </Box>
  );
};

export default GroupPositions;
